import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api, type Doc, type Id } from "../lib/api";
import { toFlatSection } from "../lib/adapters";
import { useAccess } from "../lib/access";
import { useCompany } from "../lib/company";
import { routeIdParam, searchIdParam, searchParamsWithId } from "../domain/routeParams";
import { richTextDocOrEmpty } from "../domain/richText";
import { formatCountLabel } from "../domain/text";
import { caughtErrorMessage } from "../domain/errors";
import { businessScopeDisplayName } from "../domain/businessScopes";
import {
  mappingTreeDescendantIds,
  mappingTreeSummaries,
  type MappingTreeStatus,
  type MappingTreeSummary,
} from "../domain/mappingTree";
import { RichText } from "../components/RichText";
import { Button, Icon, JurisBadge, cx } from "../components/ui";
import { MappingStatusMark } from "../components/MappingStatusMark";
import { ResponsiveWorkspacePanel } from "../components/ResponsiveWorkspacePanel";
import { MappingWorkspaceLoading, MappingWorkspaceMessage } from "../components/MappingWorkspaceState";
import { moveFocusWithin } from "../lib/accessibility";
import {
  buildSectionTree,
  filterSectionTree,
  firstVisibleSectionNode,
  legislationViewerSelectedSection,
  sectionAncestors,
  visibleTree,
  type SectionTreeNode,
} from "../domain/legislation";
import {
  policyMappingComplianceLabel,
  policyMappingComplianceToneClass,
  policyMappingConfidenceLabel,
  policyMappingConfidenceToneClass,
  policyMappingCoverageLabel,
  policyMappingCoverageToneClass,
  policyMappingMappedLabel,
  policyMappingMappedToneClass,
  policyMappingVerificationLabel,
  policyMappingVerificationToneClass,
  type PolicyMappingComplianceStatus,
} from "../domain/policyMapping";
import { documentOrderNavigation, exactMappingDecisionAvailable, mappingPanelState, mappingSectionSelection } from "../domain/mappingWorkspace";
import { persistMappingVerification } from "../lib/mappingVerification";

type MappingMatrix = NonNullable<FunctionReturnType<typeof api.policies.policyMappingMatrix>>;
type MappingRow = MappingMatrix["mappings"][number];
type SectionNode = SectionTreeNode<Id<"sections">>;
type SectionSummaryMap = ReadonlyMap<Id<"sections">, MappingTreeSummary>;

const EMPTY_SECTIONS: readonly MappingMatrix["sections"][number][] = [];
const EMPTY_MAPPINGS: readonly MappingRow[] = [];

const COMPLIANCE_PRIORITY = {
  doesNotComply: 0,
  needsLegalReview: 1,
  notMapped: 2,
  partiallyComplies: 3,
  unclear: 4,
  complies: 5,
} satisfies Record<PolicyMappingComplianceStatus, number>;

export function MappingDetail() {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const legislationId = routeIdParam<"legislation">(params, "id");
  const requestedSectionId = searchIdParam<"sections">(searchParams, "section");
  const projectId = searchIdParam<"complianceProjects">(searchParams, "project");
  const { companyId } = useCompany();
  const { isDev, isStaff } = useAccess();

  const legislation = useQuery(api.legislation.get, legislationId !== undefined ? { id: legislationId } : "skip");
  const subscriptions = useQuery(api.subscriptions.listForCompany, companyId !== null ? { companyId } : "skip");
  const openAccess = isDev || isStaff;
  const subscribed = (subscriptions ?? []).some((subscription) => subscription.legislationId === legislationId);
  const subscriptionsResolved = openAccess || subscriptions !== undefined;
  const canRead = openAccess || subscribed;

  const detail = useQuery(
    api.policies.policyMappingMatrix,
    companyId !== null && legislationId !== undefined && canRead
      ? { companyId, legislationId, ...(projectId !== null ? { projectId } : {}) }
      : "skip",
  );
  const setVerification = useMutation(api.policies.setObligationMappingVerificationStatus);

  const [selectedId, setSelectedId] = useState<Id<"sections"> | null>(null);
  const [treeOpen, setTreeOpen] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<Id<"sections">>>(() => new Set());
  const [verificationBusy, setVerificationBusy] = useState<Id<"policyObligationMappings"> | null>(null);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const treeOpenerRef = useRef<HTMLButtonElement>(null);
  const mappingOpenerRef = useRef<HTMLButtonElement>(null);
  const closeTree = useCallback(() => setTreeOpen(false), []);
  const closeMappings = useCallback(() => setMappingOpen(false), []);

  const sections = detail?.sections ?? EMPTY_SECTIONS;
  const mappings = detail?.mappings ?? EMPTY_MAPPINGS;
  const tree = useMemo(
    () => visibleTree(buildSectionTree(sections.map(toFlatSection))),
    [sections],
  );
  const mappingsBySection = useMemo(() => groupMappingsBySection(mappings), [mappings]);
  const sectionSummaries = useMemo(
    () => mappingTreeSummaries(
      sections.map((section) => ({ id: section._id, parentId: section.parentId })),
      mappings.map((mapping) => ({
        sectionId: mapping.sectionId,
        verificationStatus: mapping.verificationStatus,
      })),
    ),
    [mappings, sections],
  );

  useEffect(() => {
    if (detail === undefined || detail === null) return;
    const nextSelectedId = nextMappingDetailSectionId({
      sections,
      tree,
      requestedSectionId,
      selectedId,
      mappedSectionIds: new Set(mappingsBySection.keys()),
    });
    if (nextSelectedId !== selectedId) setSelectedId(nextSelectedId);
  }, [detail, mappingsBySection, requestedSectionId, sections, selectedId, tree]);

  const select = useCallback((sectionId: Id<"sections">) => {
    setSelectedId(sectionId);
    setSearchParams(searchParamsWithId(searchParams, "section", sectionId), { replace: true });
    if (window.matchMedia("(max-width: 760px)").matches) setTreeOpen(false);
  }, [searchParams, setSearchParams]);

  const selected = legislationViewerSelectedSection({ sections, selectedId });
  const selectedDescendantIds = useMemo(
    () => selected === null
      ? new Set<Id<"sections">>()
      : mappingTreeDescendantIds(
          sections.map((section) => ({ id: section._id, parentId: section.parentId })),
          selected._id,
        ),
    [sections, selected],
  );
  const selectedMappings = selected === null
    ? []
    : sortedMappings(mappings.filter((mapping) => selectedDescendantIds.has(mapping.sectionId)));
  const selectedStatus = selectedMappings.length === 0 ? "notMapped" : mostSevereComplianceStatus(selectedMappings);
  const selectedTreeSummary = selected === null ? undefined : sectionSummaries.get(selected._id);
  const ancestry = selected ? sectionAncestors({ section: selected, sections }) : [];
  const orderedSectionIds = flattenSectionTreeIds(tree);
  const { previous: previousSection, next: nextSection } = documentOrderNavigation({
    orderedIds: orderedSectionIds,
    currentId: selectedId,
  });
  const stats = useMemo(() => mappingMatrixStats(sections, mappings), [sections, mappings]);
  const panelState = mappingPanelState({ sectionCount: tree.length, hasSelection: selected !== null, mappingCount: selectedMappings.length });

  const updateVerification = async (mappingId: Id<"policyObligationMappings">, status: "humanVerified" | "rejected") => {
    if (companyId === null) return;
    await persistMappingVerification({
      mappingId,
      status,
      mutate: ({ mappingId: nextMappingId, status: nextStatus }) => setVerification({ companyId, mappingId: nextMappingId, status: nextStatus }),
      errorMessage: (error) => caughtErrorMessage(error, "Could not update this mapping. Try again."),
      onBusyChange: setVerificationBusy,
      onErrorChange: setVerificationError,
    });
  };

  useEffect(() => setVerificationError(null), [selectedId]);

  const toggleExpanded = useCallback((sectionId: Id<"sections">) => {
    setCollapsedIds((current) => {
      const next = new Set(current);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  const collapseAll = useCallback(() => {
    setCollapsedIds(new Set(mappingTreeParentIds(tree)));
  }, [tree]);

  const expandAll = useCallback(() => {
    setCollapsedIds(new Set());
  }, []);

  if (legislationId === undefined) return <MappingWorkspaceMessage title="Mapping source not found" message="This mapping link does not identify legislation. Return to Search and choose a source." actionLabel="Back to Search" onAction={() => navigate("/browse")} />;
  if (companyId === null) return <MappingWorkspaceMessage title="Choose a company" message="Select a company from the workspace selector in the header to open this mapping review." />;
  if (legislation === undefined) return <MappingWorkspaceLoading label="Loading legislation mapping workspace" />;
  if (legislation === null) return <MappingWorkspaceMessage title="Mapping source not found" message="This legislation is unavailable." actionLabel="Back to Search" onAction={() => navigate("/browse")} />;
  if (!canRead) {
    if (!subscriptionsResolved) return <MappingWorkspaceLoading label="Checking legislation access" />;
    return <MappingSubscribeGate legislation={legislation} onBack={() => navigate("/documents")} />;
  }
  if (detail === undefined) return <MappingWorkspaceLoading label="Loading legislation mapping workspace" />;
  if (detail === null) return <MappingWorkspaceMessage title="Mapping source unavailable" message="Lawstream could not load the mapping source. The legislation record is still available in Search." actionLabel="Back to Search" onAction={() => navigate("/browse")} />;

  return (
    <div className="ls-workspace-grid relative flex h-full min-h-0 overflow-hidden">
      <ResponsiveWorkspacePanel id="mapping-clauses" label="legislation mapping hierarchy" side="left" open={treeOpen} onClose={closeTree} staticQuery="(min-width: 761px)" openerRef={treeOpenerRef} className="!border-[var(--shell-line)] !bg-[var(--shell)] text-[var(--shell-text)]">
        <div className="border-b border-[var(--shell-line)] bg-[var(--shell-raised)] px-3 pb-3 pt-3">
          <div className="mb-2.5 flex items-center justify-between gap-2">
            <div>
              <div className="text-[12px] font-semibold text-sky-700">Legislation hierarchy</div>
              <div className="mt-1 text-[11.5px] text-slate-600">{stats.mappedSections}/{stats.totalSections} operative clauses mapped</div>
            </div>
            <div className="hidden items-center gap-2 text-[11px] font-medium sm:flex">
              <button type="button" onClick={expandAll} className="rounded px-1.5 py-1 text-slate-600 hover:bg-white hover:text-slate-900">Expand all</button>
              <button type="button" onClick={collapseAll} className="rounded px-1.5 py-1 text-slate-600 hover:bg-white hover:text-slate-900">Collapse all</button>
            </div>
          </div>
          <div className="flex h-8 items-center gap-2 rounded border border-[var(--shell-line)] bg-white px-2.5 focus-within:border-sky-600">
            <Icon name="search" size={13} className="text-slate-500" />
            <input aria-label="Filter clauses" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter source clause" className="w-full bg-transparent text-[12px] text-slate-700 outline-none placeholder:text-slate-500" />
          </div>
        </div>
        <div className="border-b border-[var(--shell-line)] bg-[var(--shell)] px-3 py-2">
          <div className="flex items-center gap-2 text-[12px] font-semibold text-slate-700">
            <TreeDocumentIcon />
            <span className="min-w-0 flex-1 truncate" title={detail.legislation.title}>{detail.legislation.title}</span>
            <MappingStatusMark status={rootMappingStatus(sectionSummaries)} />
          </div>
        </div>
        <div className="flex items-center border-b border-[var(--shell-line)] bg-[var(--shell)] px-3 py-1.5 text-[10.5px] font-medium text-slate-600">
          <span className="flex-1">Section</span>
          <span className="w-14 text-center">Review</span>
          <span className="w-10 text-right">Links</span>
        </div>
        <div className="flex-1 overflow-y-auto px-1.5 py-1.5">
          {tree.length === 0 ? (
            <p className="p-2 text-[12px] text-slate-600">No clauses available.</p>
          ) : (
            <nav
              aria-label="Legislation mapping sections"
              onKeyDown={(event) => {
                if (moveFocusWithin({ container: event.currentTarget, target: event.target, key: event.key, selector: "[data-hierarchy-item]", orientation: "vertical" })) event.preventDefault();
              }}
            >
            <MappingTree
              nodes={filterSectionTree(tree, filter)}
              depth={0}
              currentId={selectedId}
              summaries={sectionSummaries}
              collapsedIds={collapsedIds}
              forceExpanded={filter.trim() !== ""}
              onSelect={select}
              onToggleExpanded={toggleExpanded}
            />
            </nav>
          )}
        </div>
        <MappingTreeLegend />
      </ResponsiveWorkspacePanel>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="border-b border-slate-300 bg-slate-50/95 px-4 pb-3 pt-3 sm:px-5">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <button ref={treeOpenerRef} type="button" aria-label="Browse legislation hierarchy" aria-expanded={treeOpen} aria-controls="mapping-clauses" onClick={() => { setMappingOpen(false); setTreeOpen(true); }} className="-ml-1 hidden min-h-11 items-center justify-center gap-1.5 rounded px-3 text-[13px] font-semibold text-slate-600 ring-1 ring-slate-300 hover:bg-slate-100 max-[760px]:flex">
              <Icon name="layers" size={16} />
              <span>Browse hierarchy</span>
            </button>
            <button ref={mappingOpenerRef} type="button" aria-label="View document mappings" aria-expanded={mappingOpen} aria-controls="mapping-details" onClick={() => { setTreeOpen(false); setMappingOpen(true); }} className="hidden min-h-11 items-center justify-center gap-1.5 rounded px-3 text-[13px] font-semibold text-slate-600 ring-1 ring-slate-300 hover:bg-slate-100 max-[1120px]:flex">
              <Icon name="doc" size={16} />
              <span>View mappings</span>
            </button>
            <JurisBadge code={detail.legislation.jurisdiction} />
            <span className="truncate font-mono text-[12.5px] text-slate-400">{detail.legislation.reference}</span>
            {detail.project !== null && (
              <>
                <span className="text-slate-300">/</span>
                <span className="truncate text-[12.5px] font-medium text-slate-500">{detail.project.name}</span>
              </>
            )}
            {detail.project !== null && <ScopePill label={businessScopeDisplayName(detail.scope)} />}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="ls-tech-label mb-1 truncate text-slate-500">Legislation / {detail.legislation.reference}{selected ? ` / ${selected.number}` : ""}</div>
              <h2 className="font-serif text-[21px] font-semibold leading-tight tracking-tight text-slate-950 lg:text-[23px]">
                {selected ? (
                  <><span className="mr-2 font-normal text-slate-400">{selected.number}</span>{selected.title}</>
                ) : (
                  "Legislation mapping"
                )}
              </h2>
              {ancestry.length > 0 && <div className="mt-1 truncate text-[11px] text-slate-500">{ancestry.map((item) => item.title).join(" / ")}</div>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <MappingStatePill summary={selectedTreeSummary} />
              <StatusPill className={policyMappingComplianceToneClass(selectedStatus)} label={policyMappingComplianceLabel(selectedStatus)} />
              <Button className="max-[760px]:min-h-11" size="sm" variant="outline" disabled={previousSection === null} onClick={() => previousSection !== null && select(previousSection)}>Previous</Button>
              <Button className="max-[760px]:min-h-11" size="sm" disabled={nextSection === null} onClick={() => nextSection !== null && select(nextSection)}>Next clause</Button>
            </div>
          </div>
          <div className="mt-3 flex gap-2 overflow-x-auto border-t border-slate-200 pt-2" role="group" aria-label="Source mapping summary">
            <SourceReadout label="Coverage" value={`${stats.mappedSections}/${stats.totalSections}`} />
            <SourceReadout label="Mappings" value={String(mappings.length)} />
            <SourceReadout label="Review" value={String(mappings.filter((mapping) => mapping.verificationStatus === "aiUnverified").length)} attention={mappings.some((mapping) => mapping.verificationStatus === "aiUnverified")} />
            <SourceReadout label="Selected" value={String(selectedMappings.length)} />
          </div>
        </div>

        <div className="relative flex min-h-0 flex-1 overflow-hidden">
          <div className="ls-workspace-grid min-h-0 min-w-0 flex-1 overflow-y-auto">
            <div className="ls-document-surface mx-auto min-h-full max-w-[820px] border-x border-slate-300 px-6 py-8 sm:px-10 lg:px-14">
              {selected === null ? (
                <p className="text-slate-400">Select a clause from the contents.</p>
              ) : (
                <div className="ls-legal">
                  {selected.nodeType === "section" ? (
                    <RichText doc={richTextDocOrEmpty(selected.content)} legal />
                  ) : (
                    <div>
                      <p className="text-[13px] leading-relaxed text-slate-500">
                        This heading contains {formatCountLabel(selectedDescendantIds.size - 1, "nested clause")}. Its mapping status is rolled up from every mapped descendant.
                      </p>
                      {selectedMappings.length > 0 && (
                        <p className="mt-3 text-[13px] leading-relaxed text-slate-500">
                          Select a leaf clause to read its legislation text, or review the document mappings for the clauses below on the right.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <ResponsiveWorkspacePanel id="mapping-details" label="document mapping details" side="right" open={mappingOpen} onClose={closeMappings} staticQuery="(min-width: 1121px)" openerRef={mappingOpenerRef} className="overflow-y-auto !bg-[#f3f6f9]">
            <div className="sticky top-0 z-10 border-b border-slate-300 bg-slate-50/95 px-4 py-3 backdrop-blur">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="mt-0.5 text-[14px] font-semibold text-slate-950">Document evidence</h3>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    {selected === null
                      ? "No clause selected"
                      : selected.nodeType === "section"
                        ? formatCountLabel(selectedMappings.length, "mapping")
                        : `${formatCountLabel(selectedMappings.length, "mapping")} across nested clauses`}
                  </p>
                </div>
                <Button size="sm" variant="outline" icon="doc" onClick={() => navigate("/documents/import")}>Register document</Button>
              </div>
            </div>
            <div className="space-y-3 p-3">
              {panelState === "noHierarchy" ? (
                <EmptyMappingPanel text="This legislation has no visible clauses to map." />
              ) : panelState === "noSelection" ? (
                <EmptyMappingPanel text="Select a clause to inspect document coverage." />
              ) : panelState === "empty" ? (
                <EmptyMappingPanel text="No document mapping is recorded for this clause." action={<Button size="sm" variant="outline" icon="doc" onClick={() => navigate("/documents/import")}>Register document</Button>} />
              ) : (
                selectedMappings.map((mapping) => (
                  <MappingCard
                    key={mapping._id}
                    mapping={mapping}
                    busy={verificationBusy === mapping._id}
                    canDecide={exactMappingDecisionAvailable({ selectedId: selected?._id ?? null, mappedSectionIds: [mapping.sectionId], leafSelected: selected?.nodeType === "section", evidenceAvailable: mapping.policySections.length > 0 })}
                    onOpenPolicy={(policyId, policySectionId) => navigate("/mappings/document/" + policyId + (policySectionId === null ? "" : "?section=" + policySectionId))}
                    onEdit={(policyId, policySectionId) => navigate("/documents/" + policyId + (policySectionId === null ? "" : "?section=" + policySectionId))}
                    onVerify={() => void updateVerification(mapping._id, "humanVerified")}
                    onReject={() => void updateVerification(mapping._id, "rejected")}
                  />
                ))
              )}
              {verificationError !== null && <p className="rounded bg-rose-50 px-3 py-2 text-[12px] text-rose-700 ring-1 ring-rose-200" role="alert">{verificationError}</p>}
            </div>
          </ResponsiveWorkspacePanel>
        </div>
      </div>
    </div>
  );
}

function MappingTree({
  nodes,
  depth,
  currentId,
  summaries,
  collapsedIds,
  forceExpanded,
  onSelect,
  onToggleExpanded,
}: {
  readonly nodes: readonly SectionNode[];
  readonly depth: number;
  readonly currentId: Id<"sections"> | null;
  readonly summaries: SectionSummaryMap;
  readonly collapsedIds: ReadonlySet<Id<"sections">>;
  readonly forceExpanded: boolean;
  readonly onSelect: (id: Id<"sections">) => void;
  readonly onToggleExpanded: (id: Id<"sections">) => void;
}) {
  return (
    <div className={cx(depth > 0 && "ml-[11px] border-l border-[var(--shell-line)] pl-[3px]")}>
      {nodes.map((node) => {
        const isLeaf = node.nodeType === "section";
        const active = node.id === currentId;
        const summary = summaries.get(node.id);
        const hasChildren = node.children.length > 0;
        const expanded = forceExpanded || !collapsedIds.has(node.id);
        return (
          <div key={node.id}>
            <div
              className={cx(
                "group relative flex h-[30px] w-full items-center border-b border-transparent pr-1 transition-colors",
                active ? "bg-sky-100 ring-1 ring-inset ring-sky-200" : "hover:bg-white",
              )}
            >
              {active && <span className="absolute inset-y-0 left-0 w-0.5 bg-sky-700" />}
              {hasChildren ? (
                <button
                  type="button"
                  aria-label={expanded ? `Collapse ${node.title}` : `Expand ${node.title}`}
                  aria-expanded={expanded}
                  onClick={() => onToggleExpanded(node.id)}
                  className="grid h-[28px] w-5 shrink-0 place-items-center text-slate-500 hover:bg-white hover:text-slate-700"
                >
                  <Icon name="chevron" size={13} className={cx("transition-transform", expanded && "rotate-90")} />
                </button>
              ) : (
                <span className="w-5 shrink-0" />
              )}
              <button
                type="button"
                data-hierarchy-item
                aria-current={active ? "location" : undefined}
                onClick={() => onSelect(node.id)}
                className="flex min-w-0 flex-1 items-center gap-1.5 self-stretch text-left"
              >
                <TreeNodeIcon leaf={isLeaf} expanded={expanded} />
                <span className={cx(
                  "min-w-0 flex-1 truncate",
                  node.nodeType === "part"
                    ? "text-[11.5px] font-semibold text-slate-700"
                    : !isLeaf
                      ? "text-[12px] font-semibold text-slate-700"
                      : active
                        ? "text-[12px] font-medium text-sky-700"
                        : "text-[12px] text-slate-600 group-hover:text-slate-900",
                )}>
                  {node.number.trim() !== "" ? `${node.number} — ` : ""}{node.title}
                </span>
                <span className="flex w-14 shrink-0 justify-center"><MappingStatusMark status={summary?.status ?? "unmapped"} /></span>
                <span className="w-10 shrink-0 text-right font-mono text-[9px] tabular-nums text-slate-600">
                  {summary?.mappingCount ?? 0}
                </span>
              </button>
            </div>
            {hasChildren && expanded && (
              <MappingTree
                nodes={node.children}
                depth={depth + 1}
                currentId={currentId}
                summaries={summaries}
                collapsedIds={collapsedIds}
                forceExpanded={forceExpanded}
                onSelect={onSelect}
                onToggleExpanded={onToggleExpanded}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function TreeDocumentIcon() {
  return (
    <span className="relative h-3.5 w-4 shrink-0 rounded-[2px] bg-slate-500 before:absolute before:-top-0.5 before:left-0.5 before:h-1 before:w-2 before:rounded-t-[2px] before:bg-slate-500" aria-hidden="true" />
  );
}

function TreeNodeIcon({ leaf, expanded }: { readonly leaf: boolean; readonly expanded: boolean }) {
  if (leaf) {
    return (
      <span className="relative h-3.5 w-3 shrink-0 rounded-[1px] border border-sky-600/70 bg-sky-100 before:absolute before:right-0 before:top-0 before:h-1 before:w-1 before:border-b before:border-l before:border-sky-600/70" aria-hidden="true" />
    );
  }
  return (
    <span className={cx(
      "relative h-3 w-4 shrink-0 rounded-[2px] before:absolute before:-top-0.5 before:left-0.5 before:h-1 before:w-2 before:rounded-t-[2px]",
      expanded ? "bg-slate-500 before:bg-slate-500" : "bg-slate-600 before:bg-slate-600",
    )} aria-hidden="true" />
  );
}

function MappingTreeLegend() {
  return (
    <div className="border-t border-[var(--shell-line)] bg-[var(--shell)] px-4 py-3">
      <div className="ls-tech-label mb-2 text-slate-600">Map status</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[10.5px] font-medium text-slate-600">
        <LegendItem status="approved" label="Verified" />
        <LegendItem status="unapproved" label="Review needed" />
        <LegendItem status="mixed" label="Mixed status" />
        <LegendItem status="unmapped" label="Not mapped" />
      </div>
    </div>
  );
}

function LegendItem({ status, label }: { readonly status: MappingTreeStatus; readonly label: string }) {
  return <div className="flex items-center gap-1.5"><MappingStatusMark status={status} /><span>{label}</span></div>;
}

function SourceReadout({ label, value, attention = false }: { readonly label: string; readonly value: string; readonly attention?: boolean }) {
  return (
    <div className="ls-readout shrink-0">
      <span className="text-[11px] font-medium text-slate-500">{label}</span>
      <span className={cx("ls-readout-value", attention && "!text-amber-700")}>{value}</span>
    </div>
  );
}

function MappingStatePill({ summary }: { readonly summary: MappingTreeSummary | undefined }) {
  const status = summary?.status ?? "unmapped";
  const label = {
    unmapped: "Not mapped",
    unapproved: "Review needed",
    approved: "Verified",
    mixed: "Mixed status",
  }[status];
  return (
    <span className="inline-flex items-center gap-1.5 rounded-[3px] bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200">
      <MappingStatusMark status={status} />
      {label}
      {(summary?.mappingCount ?? 0) > 0 && <span className="font-mono text-[10px] text-slate-400">({summary?.mappingCount})</span>}
    </span>
  );
}

function MappingCard({ mapping, busy, canDecide, onOpenPolicy, onEdit, onVerify, onReject }: {
  mapping: MappingRow;
  busy: boolean;
  canDecide: boolean;
  onOpenPolicy: (policyId: Id<"companyPolicies">, sectionId: Id<"companyPolicySections"> | null) => void;
  onEdit: (policyId: Id<"companyPolicies">, sectionId: Id<"companyPolicySections"> | null) => void;
  onVerify: () => void;
  onReject: () => void;
}) {
  return (
    <article aria-busy={busy} className="border border-slate-300 bg-white p-3 shadow-[0_1px_0_rgba(15,23,42,0.04)]">
      <div className="flex flex-wrap gap-1.5">
        <StatusPill className={policyMappingMappedToneClass(mapping.policySectionIds.length)} label={policyMappingMappedLabel(mapping.policySectionIds.length)} />
        <StatusPill className={policyMappingComplianceToneClass(mapping.complianceStatus)} label={policyMappingComplianceLabel(mapping.complianceStatus)} />
        <StatusPill className={policyMappingVerificationToneClass(mapping.verificationStatus)} label={policyMappingVerificationLabel(mapping.verificationStatus)} />
        <StatusPill className={policyMappingConfidenceToneClass(mapping.confidence)} label={policyMappingConfidenceLabel(mapping.confidence)} />
      </div>

      <div className="mt-3">
        <button className="max-w-full truncate text-left text-[14px] font-semibold text-slate-900 hover:text-[var(--accent-dark)]" onClick={() => onOpenPolicy(mapping.policyId, mapping.policySectionIds[0] ?? null)}>
          {mapping.policy?.title ?? "Company document"}
        </button>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-slate-400">
          <span>{mapping.policyLocation}</span>
          {mapping.scope !== null && <><span className="h-1 w-1 rounded-full bg-slate-300" /><span>{businessScopeDisplayName(mapping.scope)}</span></>}
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {mapping.policySections.length === 0 ? (
          <div className="border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-[12.5px] leading-snug text-slate-500">No matching document clause is linked.</div>
        ) : (
          mapping.policySections.map((section) => (
            <div key={section._id} className="border-l-2 border-sky-500 bg-slate-50 px-3 py-2">
              <div className="text-[12.5px] font-semibold text-slate-800">{section.heading}</div>
              <p className="mt-1 whitespace-pre-wrap text-[12.5px] leading-relaxed text-slate-600">{section.body}</p>
            </div>
          ))
        )}
      </div>

      <MappingTextBlock label="Rationale" text={mapping.rationale} />
      {mapping.gap !== "" && <MappingTextBlock label="Gap" text={mapping.gap} tone="red" />}
      {mapping.recommendedAction !== "" && <MappingTextBlock label="Next action" text={mapping.recommendedAction} />}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
        <StatusPill className={policyMappingCoverageToneClass(mapping.coverageStatus)} label={policyMappingCoverageLabel(mapping.coverageStatus)} />
        {canDecide && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" icon="pencil" onClick={() => onEdit(mapping.policyId, mapping.policySectionIds[0] ?? null)}>Edit mapping</Button>
            <Button className="max-[760px]:min-h-11" size="sm" icon="check" disabled={busy} onClick={onVerify}>{busy ? "Saving…" : "Verify"}</Button>
            <Button className="max-[760px]:min-h-11" size="sm" variant="ghost" icon="trash" disabled={busy} onClick={onReject}>Reject</Button>
          </div>
        )}
      </div>
      {!canDecide && <p className="mt-2 text-[11.5px] text-slate-400">Select the exact legislative clause with linked document wording to verify or reject this mapping.</p>}
    </article>
  );
}

function MappingTextBlock({ label, text, tone = "slate" }: { readonly label: string; readonly text: string; readonly tone?: "slate" | "red" }) {
  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <div className="text-[11px] font-semibold text-slate-500">{label}</div>
      <p className={cx("mt-1 text-[12px] leading-relaxed", tone === "red" ? "text-red-700" : "text-slate-600")}>{text}</p>
    </div>
  );
}

function EmptyMappingPanel({ text, action }: { readonly text: string; readonly action?: React.ReactNode }) {
  return (
    <div className="border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-[13px] text-slate-500">
      <div className="ls-tech-label mb-2 text-slate-500">Document mappings</div>
      <p>{text}</p>
      {action !== undefined && <div className="mt-4">{action}</div>}
    </div>
  );
}

function StatusPill({ className, label }: { readonly className: string; readonly label: string }) {
  return <span className={cx("inline-flex rounded-[3px] px-1.5 py-0.5 text-[10px] font-semibold ring-1", className)}>{label}</span>;
}

function ScopePill({ label }: { readonly label: string }) {
  return <span className="rounded bg-white px-1.5 py-0.5 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200">{label}</span>;
}

function MappingSubscribeGate({ legislation, onBack }: { readonly legislation: Doc<"legislation">; readonly onBack: () => void }) {
  return (
    <div className="mx-auto max-w-[560px] px-6 py-16 text-center">
      <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-[var(--accent-soft)] text-[var(--accent-dark)]">
        <Icon name="lock" size={22} />
      </div>
      <div className="mb-2 flex items-center justify-center gap-2">
        <JurisBadge code={legislation.jurisdiction} />
        <span className="font-mono text-[12.5px] text-slate-400">{legislation.reference}</span>
      </div>
      <h2 className="font-serif text-[22px] font-semibold text-slate-900">{legislation.title}</h2>
      <p className="mt-2 text-sm text-slate-500">Your organisation is not subscribed to this legislation.</p>
      <div className="mt-5 flex justify-center gap-2">
        <Button icon="layers" onClick={onBack}>Back to Documents</Button>
      </div>
    </div>
  );
}

const groupMappingsBySection = (mappings: readonly MappingRow[]): Map<Id<"sections">, MappingRow[]> => {
  const bySection = new Map<Id<"sections">, MappingRow[]>();
  for (const mapping of mappings) {
    const current = bySection.get(mapping.sectionId) ?? [];
    bySection.set(mapping.sectionId, [...current, mapping]);
  }
  return bySection;
};

const mostSevereComplianceStatus = (mappings: readonly MappingRow[]): PolicyMappingComplianceStatus => {
  let current: PolicyMappingComplianceStatus = "complies";
  for (const mapping of mappings) {
    if (COMPLIANCE_PRIORITY[mapping.complianceStatus] < COMPLIANCE_PRIORITY[current]) current = mapping.complianceStatus;
  }
  return current;
};

const sortedMappings = (mappings: readonly MappingRow[]): MappingRow[] =>
  [...mappings].sort((a, b) => {
    const status = COMPLIANCE_PRIORITY[a.complianceStatus] - COMPLIANCE_PRIORITY[b.complianceStatus];
    if (status !== 0) return status;
    return b.updatedAt - a.updatedAt;
  });

const firstMappedSectionNode = (nodes: readonly SectionNode[], mappedSectionIds: ReadonlySet<Id<"sections">>): SectionNode | null => {
  for (const node of nodes) {
    if (node.nodeType === "section" && mappedSectionIds.has(node.id)) return node;
    const child = firstMappedSectionNode(node.children, mappedSectionIds);
    if (child !== null) return child;
  }
  return null;
};

const mappingTreeParentIds = (nodes: readonly SectionNode[]): Id<"sections">[] => {
  const ids: Id<"sections">[] = [];
  for (const node of nodes) {
    if (node.children.length === 0) continue;
    ids.push(node.id, ...mappingTreeParentIds(node.children));
  }
  return ids;
};

const flattenSectionTreeIds = (nodes: readonly SectionNode[]): Id<"sections">[] =>
  nodes.flatMap((node) => [node.id, ...flattenSectionTreeIds(node.children)]);

const rootMappingStatus = (summaries: SectionSummaryMap): MappingTreeStatus => {
  let approved = false;
  let unapproved = false;
  let unmapped = summaries.size === 0;
  for (const summary of summaries.values()) {
    if (summary.status === "approved" || summary.status === "mixed") approved = true;
    if (summary.status === "unapproved" || summary.status === "mixed") unapproved = true;
    if (summary.status === "unmapped") unmapped = true;
  }
  if ((approved || unapproved) && unmapped) return "mixed";
  if (approved && unapproved) return "mixed";
  if (approved) return "approved";
  if (unapproved) return "unapproved";
  return "unmapped";
};

const sectionIsSelectable = (sections: readonly MappingMatrix["sections"][number][], sectionId: Id<"sections"> | null): boolean =>
  sectionId !== null && sections.some((section) => section._id === sectionId && !section.hidden);

const nextMappingDetailSectionId = ({
  sections,
  tree,
  requestedSectionId,
  selectedId,
  mappedSectionIds,
}: {
  readonly sections: readonly MappingMatrix["sections"][number][];
  readonly tree: readonly SectionNode[];
  readonly requestedSectionId: Id<"sections"> | null;
  readonly selectedId: Id<"sections"> | null;
  readonly mappedSectionIds: ReadonlySet<Id<"sections">>;
}): Id<"sections"> | null => {
  return mappingSectionSelection({
    orderedIds: sections.filter((section) => !section.hidden).map((section) => section._id),
    requestedId: sectionIsSelectable(sections, requestedSectionId) ? requestedSectionId : null,
    currentId: sectionIsSelectable(sections, selectedId) ? selectedId : null,
    preferredId: firstMappedSectionNode(tree, mappedSectionIds)?.id ?? firstVisibleSectionNode(tree)?.id ?? null,
  });
};

const mappingMatrixStats = (sections: readonly MappingMatrix["sections"][number][], mappings: readonly MappingRow[]) => {
  const totalSections = sections.filter((section) => section.nodeType === "section" && !section.hidden).length;
  const mappedSections = new Set(mappings.filter((mapping) => mapping.verificationStatus !== "rejected").map((mapping) => mapping.sectionId)).size;
  return {
    totalSections,
    mappedSections,
    complies: mappings.filter((mapping) => mapping.complianceStatus === "complies").length,
    doesNotComply: mappings.filter((mapping) => mapping.complianceStatus === "doesNotComply").length,
    needsReview: mappings.filter((mapping) =>
      mapping.complianceStatus === "partiallyComplies" ||
      mapping.complianceStatus === "unclear" ||
      mapping.complianceStatus === "needsLegalReview" ||
      mapping.complianceStatus === "notMapped",
    ).length,
  };
};
